import { Discount } from "./discount"
import { FeeResolutionContext } from "./fee-price-resolver"
import { PriceUtils } from "./price-utils"
import { ServiceAction } from "./service-part-action"

interface FeeBaseProduct {
  price: number
  quantity: number
}

interface FeeBasePartAction {
  action: ServiceAction
  laborPrice: number | null
  laborPriceDiscount: Discount | null
  products: FeeBaseProduct[]
}

interface FeeBaseManualServiceAction {
  laborPrice: number
  laborPriceDiscount: Discount | null
  products: FeeBaseProduct[]
}

interface FeeBasePackage {
  laborPrice: number
  laborPriceDiscount: Discount | null
  actions: { action: ServiceAction }[]
}

export interface AddonFeeContextInput {
  /**
   * Everything already on the work order, minus the fees themselves. The fees
   * are added back by `resolveJobFeePrices`, which prices the consumables fee
   * from the services and every other fee.
   */
  existingNonFeeTotal: number
  partActions: FeeBasePartAction[]
  manualServiceActions: FeeBaseManualServiceAction[]
  packages: FeeBasePackage[]
}

/**
 * Builds the `{ orderTotal, changeActionCount }` context that
 * `resolveStandardFeePrice` needs for an addon order's dynamic fees
 * (PERCENTAGE / PER_CHANGE).
 *
 * Shared by the API (when it snapshots the fees at submission) and the CRM
 * (when it previews the price while the mechanic edits rows), so the number the
 * mechanic sees is the number that gets charged. Same reasoning as
 * `addonOrderNeedsCustomerDecision`: the two used to be written twice and
 * drifted.
 *
 * CHANGE actions inside packages count too, matching what
 * `WorkOrderService.recalculateDynamicFees` does when it re-resolves the price
 * after the customer has answered.
 */
export function buildAddonFeeContext(
  input: AddonFeeContextInput,
): FeeResolutionContext {
  const productsCost = (products: FeeBaseProduct[]): number =>
    products.reduce((sum, product) => sum + product.price * product.quantity, 0)

  let addonTotal = 0

  for (const partAction of input.partActions) {
    addonTotal += PriceUtils.applyDiscount(
      partAction.laborPrice ?? 0,
      partAction.laborPriceDiscount,
    )
    addonTotal += productsCost(partAction.products)
  }

  for (const manualServiceAction of input.manualServiceActions) {
    addonTotal += PriceUtils.applyDiscount(
      manualServiceAction.laborPrice,
      manualServiceAction.laborPriceDiscount,
    )
    addonTotal += productsCost(manualServiceAction.products)
  }

  for (const servicePackage of input.packages) {
    addonTotal += PriceUtils.applyDiscount(
      servicePackage.laborPrice,
      servicePackage.laborPriceDiscount,
    )
  }

  const standaloneChangeCount = input.partActions.filter(
    (partAction) => partAction.action === ServiceAction.CHANGE,
  ).length
  const packageChangeCount = input.packages.reduce(
    (sum, servicePackage) =>
      sum +
      servicePackage.actions.filter(
        (action) => action.action === ServiceAction.CHANGE,
      ).length,
    0,
  )

  return {
    orderTotal: input.existingNonFeeTotal + addonTotal,
    changeActionCount: standaloneChangeCount + packageChangeCount,
  }
}
